"use strict";
/**
 * APITable <https://github.com/apitable/apitable>
 * Copyright (C) 2022 APITable Ltd. <https://apitable.com>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.RatingField = void 0;
const joi_1 = __importDefault(require("joi"));
const lodash_1 = require("lodash");
const field_types_1 = require("../../types/field_types");
const convert_1 = require("../../utils/convert");
const datasheet_1 = require("../datasheet");
const number_base_field_1 = require("./number_base_field");
const types_1 = require("../../types");
const utils_1 = require("../../model/utils");
const emojis_config_1 = require("../../config/emojis_config");
const validate_schema_1 = require("./validate_schema");
class RatingField extends number_base_field_1.NumberBaseField {
    constructor(field, state) {
        super(field, state);
        this.field = field;
    }
    validateProperty() {
        return RatingField.propertySchema.validate(this.field.property);
    }
    get apiMetaProperty() {
        return {
            icon: (0, utils_1.getEmojiIconNativeString)(this.field.property.icon),
            max: this.field.property.max,
        };
    }
    static createDefault(fieldMap) {
        return {
            id: datasheet_1.DatasheetActions.getNewFieldId(fieldMap),
            type: field_types_1.FieldType.Rating,
            name: datasheet_1.DatasheetActions.getDefaultFieldName(fieldMap),
            property: this.defaultProperty(),
        };
    }
    static defaultProperty() {
        return { icon: 'star', max: 5 };
    }
    cellValueToString(cellValue) {
        if (this.validate(cellValue)) {
            return cellValue.toString();
        }
        return null;
    }
    stdValueToCellValue(stdVal) {
        const { data, sourceType } = stdVal;
        if (data.length === 0) {
            return null;
        }
        const cValue = number_base_field_1.commonNumberFields.has(sourceType) ? data[0].value : (0, convert_1.str2number)(data[0].text);
        if (!cValue)
            return null;
        if (cValue && cValue >= this.field.property.max)
            return this.field.property.max;
        if (cValue != null && cValue < 1)
            return null;
        return parseInt(cValue, 10);
    }
    /**
     * Cancel the verification of property.max, and call cellValueToString during statistics.
     * The result of the summation may be greater than max. Values exceeding max have been handled at the UI layer.
     */
    validate(value) {
        // && value <= this.field.property.max
        return (0, lodash_1.isNumber)(value) && !Number.isNaN(value) && value > 0;
    }
    isMeetFilter(operator, cellValue, conditionValue) {
        if (conditionValue == null) {
            // Score filter null == 0
            const filterValue = 0;
            const cv = cellValue || 0;
            switch (operator) {
                case types_1.FOperator.Is:
                    return cellValue === conditionValue;
                case types_1.FOperator.IsNot:
                    return cellValue !== conditionValue;
                case types_1.FOperator.IsGreater:
                    return cv > filterValue;
                case types_1.FOperator.IsGreaterEqual:
                    return cv >= filterValue;
                case types_1.FOperator.IsLess:
                    return cv < filterValue;
                case types_1.FOperator.IsLessEqual:
                    return cv <= filterValue;
                default:
                    return number_base_field_1.NumberBaseField._isMeetFilter(operator, cellValue, conditionValue);
            }
        }
        return number_base_field_1.NumberBaseField._isMeetFilter(operator, cellValue, conditionValue);
    }
    get openFieldProperty() {
        const { max, icon } = this.field.property;
        return {
            icon: (0, utils_1.getEmojiIconNativeString)(icon),
            max,
        };
    }
    validateUpdateOpenProperty(updateProperty) {
        const result = RatingField.propertySchema.validate(updateProperty);
        if (!result.error && !emojis_config_1.EmojisConfig[updateProperty.icon]) {
            return (0, validate_schema_1.joiErrorResult)('icon is not Emoji slug');
        }
        return result;
    }
}
exports.RatingField = RatingField;
RatingField.propertySchema = joi_1.default.object({
    icon: joi_1.default.string().required(),
    max: joi_1.default.number().min(0).max(10).required(),
}).required();
//# sourceMappingURL=rating_field.js.map